import React, { useState, useEffect } from 'react';
import { XMarkIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { api } from '../../../shared/services/api/axiosInstance';
import { API_ENDPOINTS } from '../../../shared/services/api/endpoints';
import { ReportService } from '../../../shared/services/reportService';
import {
  REPORT_TYPE_OPTIONS,
  SCOPE_LEVEL_OPTIONS,
  NO_DATE_RANGE_TYPES,
  STATES,
} from '../config/reportsConfig';

const INITIAL_FORM = {
  reportType: 'TOTAL_STOCK',
  scopeLevel: 'STATE',
  scopeId: '',
  startDate: '',
  endDate: '',
};

export default function GenerateReportModal({ onClose, onSuccess }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [scopeOptions, setScopeOptions] = useState([]);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const needsDateRange = !NO_DATE_RANGE_TYPES.has(form.reportType);
  const scopeIdOptional = form.reportType === 'STOCK_SUMMARY';

  useEffect(() => {
    if (form.scopeLevel === 'STATE') {
      setScopeOptions(STATES);
      setForm((f) => ({ ...f, scopeId: STATES[0]?.value ?? '' }));
      return;
    }
    setScopeOptions([]);
    setForm((f) => ({ ...f, scopeId: '' }));
    setScopeLoading(true);

    const fetchScope = async () => {
      try {
        let url = '';
        if (form.scopeLevel === 'DISTRICT') url = API_ENDPOINTS.DISTRICTS.ADMIN;
        else if (form.scopeLevel === 'TALUKA') url = API_ENDPOINTS.TALUKAS.ADMIN;
        else if (form.scopeLevel === 'CENTER') url = API_ENDPOINTS.CENTERS.ALL;

        const res = await api.get(url);
        const data = res.data?.data ?? res.data ?? [];
        const list = Array.isArray(data) ? data : [];
        setScopeOptions(
          list.map((item) => ({
            value: String(item.id ?? item._id),
            label: item.name ?? item.centerName ?? item.districtName ?? item.talukaName ?? String(item.id),
          }))
        );
      } catch {
        setScopeOptions([]);
      } finally {
        setScopeLoading(false);
      }
    };

    fetchScope();
  }, [form.scopeLevel]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const payload = {
      reportType: form.reportType,
      scopeLevel: form.scopeLevel,
      ...(form.scopeId ? { scopeId: form.scopeId } : {}),
      ...(needsDateRange && form.startDate && form.endDate
        ? { startDate: form.startDate, endDate: form.endDate }
        : {}),
    };

    setSubmitting(true);
    try {
      const res = await ReportService.generate(payload);
      onSuccess?.(res.data);
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Failed to generate report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#4057fe] flex items-center justify-center">
              <DocumentChartBarIcon className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Generate Report</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Report Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Report Type</label>
            <div className="flex flex-wrap gap-2">
              {REPORT_TYPE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="reportType"
                    value={opt.value}
                    checked={form.reportType === opt.value}
                    onChange={() => set('reportType', opt.value)}
                    className="accent-[#4057fe]"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Scope Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Scope Level</label>
            <select
              value={form.scopeLevel}
              onChange={(e) => set('scopeLevel', e.target.value)}
              className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#4057fe]/30"
            >
              {SCOPE_LEVEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Scope ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {form.scopeLevel === 'STATE' ? 'State' : form.scopeLevel === 'DISTRICT' ? 'District' : form.scopeLevel === 'TALUKA' ? 'Taluka' : 'Center'}
              {scopeIdOptional && form.scopeLevel !== 'STATE' && (
                <span className="ml-1 text-xs text-gray-400 font-normal">(optional — leave blank for all)</span>
              )}
            </label>
            <select
              value={form.scopeId}
              onChange={(e) => set('scopeId', e.target.value)}
              disabled={scopeLoading || scopeOptions.length === 0}
              required={!scopeIdOptional || form.scopeLevel === 'STATE'}
              className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#4057fe]/30 disabled:opacity-50"
            >
              {scopeLoading
                ? <option>Loading…</option>
                : <>
                    {scopeIdOptional && form.scopeLevel !== 'STATE' && (
                      <option value="">— All {form.scopeLevel === 'DISTRICT' ? 'Districts' : form.scopeLevel === 'TALUKA' ? 'Talukas' : 'Centers'} —</option>
                    )}
                    {scopeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </>
              }
            </select>
          </div>

          {/* Date Range — hidden for LOW_STOCK / STOCK_SUMMARY */}
          {needsDateRange && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => set('startDate', e.target.value)}
                  required
                  className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#4057fe]/30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                <input
                  type="date"
                  value={form.endDate}
                  onChange={(e) => set('endDate', e.target.value)}
                  required
                  min={form.startDate || undefined}
                  className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#4057fe]/30"
                />
              </div>
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="text-sm px-5 py-2 rounded-lg bg-[#4057fe] text-white hover:bg-[#3347e0] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting && <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
              {submitting ? 'Queuing…' : 'Generate Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
